import React, { createContext, useContext, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

/**
 * Context to manage a single dialog instance across the app
 */
const InfoDialogContext = createContext(null);

/**
 * Provider component - wrap your app or page with this
 */
export const InfoDialogProvider = ({ children }) => {
  const [dialogContent, setDialogContent] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const openDialog = (content) => {
    setDialogContent(content);
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    // Clear content after animation completes
    setTimeout(() => setDialogContent(null), 200);
  };

  return (
    <InfoDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      <Dialog open={isOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md md:max-w-lg lg:max-w-xl max-h-[calc(100vh-4rem)] overflow-y-auto">
          {dialogContent}
        </DialogContent>
      </Dialog>
    </InfoDialogContext.Provider>
  );
};

/**
 * Hook to access dialog context
 */
const useInfoDialog = () => {
  const context = useContext(InfoDialogContext);
  if (!context) {
    throw new Error("useInfoDialog must be used within InfoDialogProvider");
  }
  return context;
};

/**
 * Reusable styled info text component
 *
 * @param {string} text - The text to display
 * @param {React.ReactNode} dialogContent - Content to show in dialog
 * @param {string} className - Optional additional classes
 */
export const InfoText = ({ text, dialogContent, className = "" }) => {
  const { openDialog } = useInfoDialog();

  return (
    <button
      type="button"
      onClick={() => openDialog(dialogContent)}
      className={`inline-block bg-transparent border-0 p-0 m-0 font-inherit text-inherit leading-inherit align-baseline underline decoration-double underline-offset-2 cursor-pointer hover:text-[#6b21a8] transition-colors ${className}`}
    >
      {text}
    </button>
  );
};
